import { Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { MatTabGroup } from '@angular/material/tabs';
import { TabChangeService } from '../_services/shared/tab-change.service';
import { DialogService } from '../_services/shared/dialog.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
   * @description:Applied to tab group element to keep track of unsaved form changes in tab to warn user navigating away from active tab. 
   *              This works in conjunction with deactivation-aware.directive and TabChangeService
   */
@Directive({
    selector: '[wfdssConfirmTabChange]',
    standalone: false
})
export class ConfirmTabChangeDirective implements OnInit, OnDestroy {
  /**
   * @description: Mandator field. It is used for listening in to tab change and warn user if there is unsaved change.
   */
  @Input() tabGroup!: MatTabGroup;
  public _destroying = new Subject<void>();
  
  private lastIndex = 0;

  constructor(private tabService: TabChangeService, private dialogService: DialogService) {}

  ngOnInit(): void {
    this.lastIndex = this.tabGroup.selectedIndex ?? 0;
    this.tabService.setCurrentIndex(this.lastIndex);

    this.tabGroup.selectedIndexChange.pipe(takeUntil(this._destroying)).subscribe((nextIndex) => {    
      this.handleTabChange(nextIndex);
    });
  }

  async handleTabChange(nextIndex) {
    const canLeave = this.tabService.canDeactivateTab();
      
    if (!canLeave && nextIndex!=this.lastIndex) {
      const discardAndContinue = await this.dialogService.openConfirmDialog('You have unsaved changes that will be lost when you navigate away from this page. Press OK to continue (your work will not be saved), or Cancel to stay on the current page.', 'Ok', 'Cancel').afterClosed().toPromise();        
      if(!discardAndContinue) {
        setTimeout(() => {    
          this.tabGroup.selectedIndex = this.lastIndex;        
          this.tabService.setCurrentIndex(this.lastIndex);
        });
        return;
      } else {
        this.tabService.markPristineIfApplicable(this.lastIndex);
      } 
    }
    
    this.lastIndex = nextIndex;
    this.tabService.setCurrentIndex(nextIndex);
  }

  ngOnDestroy(): void {
    this.tabService.clear();
    this._destroying.next();    
    this._destroying.complete();
  }
}
